/**
 * Pharmacy lookup for the medication screens.
 *
 * `AddMedicationForm.vue` and `MedicationPharmacyTabPanel.vue` each held their
 * own copy of a `axios.get(CURR_API + '/pharmacies.php?…')` — a URL that matches
 * none of the legacy bridge's patterns (`legacyBridge.js` claims only
 * `tswebhook.php`, `NPILookup.php`, `index.php`, `/online/ajax` and
 * `/sessionless/`), so the request went to the network and, with no keyon
 * container in the ported compose file, 502'd on every keystroke.
 *
 * The replacement is `GET /pharmacy-registry-search` (catalogue row 150), which
 * is the same NPPES proxy moved server-side: it keeps `keyon/pharmacies.php`'s
 * input sanitisers and its telephone post-filter, and adds the timeout, the
 * cache and the Bearer token. Calling CMS from the browser per keystroke spent a
 * rate budget CMS enforces per source IP.
 *
 * One module rather than two component-local copies, because the field names on
 * the wire changed and the translation has to be stated once.
 */

import { searchPharmacyRegistry } from '@/api2/medications';

/**
 * One pharmacy in the shape the two accordions already render.
 *
 * The new resource spells the ZIP `postal_code` (its convention throughout) and
 * both templates bind `address.postalCode` (keyon's). Renaming it here rather
 * than in the templates keeps the change to one line and one place; `fax` and
 * `entity_type` are new and ride along because the edit panel can use them.
 */
function toPharmacyCard(row) {
  const address = row.address || {};

  return {
    npi: row.npi || '',
    name: row.name || '',
    telephone: row.telephone || null,
    fax: row.fax || null,
    entityType: row.entity_type || '',
    address: {
      line1: address.line1 || '',
      line2: address.line2 || '',
      city: address.city || '',
      state: address.state || '',
      postalCode: address.postal_code || '',
    },
  };
}

/**
 * Search the federal pharmacy registry.
 *
 * Takes the search box's own field names, so the call sites read as they did.
 * Blank terms are dropped rather than sent empty: the endpoint counts a present
 * term towards its "at least one" rule, and an empty `name=` is not a term.
 *
 * Rejects with the client's `ApiError`. Two of its statuses are worth knowing
 * about at the call site because they are answers rather than faults: **400**
 * for a search with no usable term, and **400** for a telephone on its own —
 * NPPES cannot be searched by telephone at all, so keyon's telephone-only branch
 * (which downloaded a taxonomy page and filtered it) has no honest equivalent.
 * Both carry a sentence saying so, which is what the screens display.
 *
 * @param {{name?: string, telephone?: string, city?: string, state?: string, postal_code?: string}} criteria
 * @returns {Promise<{count: number, results: Array<object>, isStale: boolean}>}
 */
export async function searchPharmacies(criteria = {}) {
  const params = {};
  const add = (key, value) => {
    const trimmed = String(value ?? '').trim();
    if (trimmed !== '') params[key] = trimmed;
  };

  add('name', criteria.name);
  add('telephone', criteria.telephone);
  add('city', criteria.city);
  // The endpoint validates `size:2`, and the search box lets people type "ny".
  add('state', String(criteria.state ?? '').toUpperCase());
  add('postal_code', criteria.postal_code);

  const answer = await searchPharmacyRegistry(params);

  return {
    count: answer?.count ?? 0,
    results: (answer?.results || []).map(toPharmacyCard),
    // Only present when true: the registry was unreachable and this came from
    // the week-long stale cache. Worth telling the user — `NPILookup.php`
    // suppressing its errors with `@` is exactly what took that away.
    isStale: answer?.is_stale === true,
  };
}

export default { searchPharmacies };
